import Feature from 'ol/Feature';
import Geolocation from 'ol/Geolocation';
import GeoJSON from 'ol/format/GeoJSON';
import Map from 'ol/Map';
import Point from 'ol/geom/Point';
import View from 'ol/View';
import {Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style, Text} from 'ol/style';
import {OSM, Vector as VectorSource, XYZ} from 'ol/source';
import {Group as LayerGroup, Vector as VectorLayer} from 'ol/layer';
import TileLayer from 'ol/layer/WebGLTile';
import {Control, defaults as defaultControls} from 'ol/control';
import * as olextent from 'ol/extent';
import VectorImageLayer from 'ol/layer/VectorImage';
import {all as allLoadingStrategy} from 'ol/loadingstrategy';
import {toStringHDMS} from 'ol/coordinate';
import {fromLonLat, toLonLat} from 'ol/proj';

//useGeographic();

const view = new View({
  center: [2775752, 8435259],
  //center: [26.745303877900252,60.924276062199006],
  zoom: 10,
});



const restrictionStyleCache = {};
const seamarkStyleCache = {};
const DEFAULT_API_BASE_URL = '/api';
const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL || DEFAULT_API_BASE_URL);
const RESTRICTION_CACHE_DB_NAME = 'marinemap-browser-cache';
const RESTRICTION_CACHE_STORE_NAME = 'geojson';
const RESTRICTION_CACHE_KEY = 'restrictions-v1';
const RESTRICTION_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const RESTRICTION_CACHE_MAX_BYTES = 5 * 1024 * 1024;
const restrictionGeoJsonFormat = new GeoJSON();
let restrictionStatusTimeoutId = null;
const restrictionStatusActivities = new Set();
let restrictionPersistentStatus = {
  message: '',
  error: false,
};
let restrictionFlashStatus = null;
const PLACE_AUTOCOMPLETE_LAYERS = 'address,venue,street,stop,station';
const PLACE_AUTOCOMPLETE_DELAY_MS = 180;
const DEFAULT_PLACE_SEARCH_LANG = resolvePlaceSearchLanguage();

const BASE_LAYER_ID_STANDARD = 'osm-standard';
const BASE_LAYER_ID_OPENTOPO = 'opentopomap';
const OVERLAY_LAYER_ID_SPEED = 'restrictions-speed';
const OVERLAY_LAYER_ID_MOTOR = 'restrictions-motor-ban';
const OVERLAY_LAYER_ID_BUOYS = 'seamarks-buoy-markers';

const OSM_CONTRIBUTORS_ATTRIBUTION = '© OpenStreetMap contributors';
const MOBILE_LAYOUT_MEDIA_QUERY = '(max-width: 640px), (pointer: coarse)';
const ENABLE_INLINE_RESTRICTION_LABELS = !window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches;

function resolveApiBaseUrl(value) {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return DEFAULT_API_BASE_URL;
  }

  return trimmedValue.replace(/\/+$/, '');
}

function resolvePlaceSearchLanguage() {
  const language = String(navigator.language || '').toLowerCase();
  if (language.startsWith('sv')) {
    return 'sv';
  }
  if (language.startsWith('en')) {
    return 'en';
  }
  return 'fi';
}

function setRestrictionStatus({message = '', loading = false, error = false} = {}) {
  const statusElement = document.getElementById('restriction-status');
  if (!statusElement) {
    return;
  }

  statusElement.textContent = message;
  statusElement.classList.toggle('is-hidden', !message);
  statusElement.classList.toggle('is-loading', loading);
  statusElement.classList.toggle('is-error', error);
}

function clearRestrictionStatusTimer() {
  if (restrictionStatusTimeoutId !== null) {
    window.clearTimeout(restrictionStatusTimeoutId);
    restrictionStatusTimeoutId = null;
  }
}

function renderRestrictionStatus() {
  if (restrictionStatusActivities.has('restriction')) {
    setRestrictionStatus({
      message: 'Loading restriction data...',
      loading: true,
    });
    return;
  }

  if (restrictionStatusActivities.has('seamark')) {
    setRestrictionStatus({
      message: 'Loading markers...',
      loading: true,
    });
    return;
  }

  if (restrictionFlashStatus) {
    setRestrictionStatus(restrictionFlashStatus);
    return;
  }

  setRestrictionStatus(restrictionPersistentStatus);
}

function startRestrictionStatusActivity(activity) {
  restrictionStatusActivities.add(activity);
  renderRestrictionStatus();
}

function endRestrictionStatusActivity(activity) {
  restrictionStatusActivities.delete(activity);
  renderRestrictionStatus();
}

function setRestrictionPersistentStatus({message = '', error = false} = {}) {
  restrictionPersistentStatus = {
    message,
    error,
  };
  renderRestrictionStatus();
}

function setRestrictionFlashStatus({message = '', error = false, durationMs = 0} = {}) {
  clearRestrictionStatusTimer();
  restrictionFlashStatus = {
    message,
    error,
  };
  renderRestrictionStatus();

  if (durationMs > 0) {
    restrictionStatusTimeoutId = window.setTimeout(() => {
      restrictionStatusTimeoutId = null;
      restrictionFlashStatus = null;
      renderRestrictionStatus();
    }, durationMs);
  }
}

function formatRestrictionDateLabel(value) {
  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) {
    return '';
  }

  const match = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return `Data ${trimmedValue}`;
  }

  const [, year, month, day] = match;
  return `Data ${day}.${month}.${year}`;
}

function showRestrictionDateStatus(lastRefreshed) {
  const dateLabel = formatRestrictionDateLabel(lastRefreshed);
  if (!dateLabel) {
    setRestrictionPersistentStatus();
    return;
  }

  setRestrictionPersistentStatus({message: dateLabel});
}

function showRestrictionCacheStatus(lastRefreshed) {
  const dateLabel = formatRestrictionDateLabel(lastRefreshed);
  const message = dateLabel ? `Served from cache • ${dateLabel}` : 'Served from cache';
  showRestrictionDateStatus(lastRefreshed);
  setRestrictionFlashStatus({
    message,
    durationMs: 2200,
  });
}

function showRestrictionStaleCacheStatus(lastRefreshed) {
  const dateLabel = formatRestrictionDateLabel(lastRefreshed);
  const message = dateLabel
    ? `Network failed • showing stale cache • ${dateLabel}`
    : 'Network failed • showing stale cache';
  setRestrictionPersistentStatus({
    message,
    error: true,
  });
}

function openRestrictionCacheDb() {
  if (!window.indexedDB) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(RESTRICTION_CACHE_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RESTRICTION_CACHE_STORE_NAME)) {
        db.createObjectStore(RESTRICTION_CACHE_STORE_NAME, {keyPath: 'id'});
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error || new Error('Failed to open restriction cache database.'));
    };
  });
}

async function readRestrictionCacheEntry() {
  const db = await openRestrictionCacheDb();
  if (!db) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(RESTRICTION_CACHE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(RESTRICTION_CACHE_STORE_NAME);
    const request = store.get(RESTRICTION_CACHE_KEY);

    request.onsuccess = () => {
      resolve(request.result || null);
    };

    request.onerror = () => {
      reject(request.error || new Error('Failed to read restriction cache.'));
    };

    transaction.oncomplete = () => {
      db.close();
    };

    transaction.onabort = transaction.onerror = () => {
      db.close();
    };
  });
}

async function deleteRestrictionCacheEntry() {
  const db = await openRestrictionCacheDb();
  if (!db) {
    return;
  }

  await new Promise((resolve, reject) => {
    const transaction = db.transaction(RESTRICTION_CACHE_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(RESTRICTION_CACHE_STORE_NAME);
    const request = store.delete(RESTRICTION_CACHE_KEY);

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(request.error || new Error('Failed to clear restriction cache.'));
    };

    transaction.oncomplete = () => {
      db.close();
    };

    transaction.onabort = transaction.onerror = () => {
      db.close();
    };
  });
}

async function writeRestrictionCacheEntry(payloadText) {
  const compressedPayload = await compressRestrictionPayload(payloadText);
  if (getRestrictionPayloadSize(compressedPayload.payload) > RESTRICTION_CACHE_MAX_BYTES) {
    throw new Error('Restriction cache payload exceeds browser storage safety limit.');
  }

  const db = await openRestrictionCacheDb();
  if (!db) {
    return;
  }

  await new Promise((resolve, reject) => {
    const transaction = db.transaction(RESTRICTION_CACHE_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(RESTRICTION_CACHE_STORE_NAME);
    const request = store.put({
      id: RESTRICTION_CACHE_KEY,
      savedAt: Date.now(),
      compression: compressedPayload.compression,
      payload: compressedPayload.payload,
    });

    request.onsuccess = () => {
      resolve();
    };

    request.onerror = () => {
      reject(request.error || new Error('Failed to write restriction cache.'));
    };

    transaction.oncomplete = () => {
      db.close();
    };

    transaction.onabort = transaction.onerror = () => {
      db.close();
    };
  });
}

function isRestrictionCacheFresh(entry) {
  if (!entry || typeof entry.savedAt !== 'number') {
    return false;
  }

  return Date.now() - entry.savedAt <= RESTRICTION_CACHE_MAX_AGE_MS;
}

function getRestrictionPayloadSize(payload) {
  if (typeof payload === 'string') {
    return new TextEncoder().encode(payload).byteLength;
  }

  return payload?.byteLength || 0;
}

async function compressRestrictionPayload(payloadText) {
  if (typeof CompressionStream === 'undefined') {
    return {
      compression: 'identity',
      payload: payloadText,
    };
  }

  const compressedBuffer = await new Response(
    new Blob([payloadText]).stream().pipeThrough(new CompressionStream('gzip')),
  ).arrayBuffer();

  return {
    compression: 'gzip',
    payload: compressedBuffer,
  };
}

async function expandRestrictionPayload(entry) {
  if (!entry) {
    return null;
  }

  if (entry.compression === 'gzip') {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error('This browser cannot read the cached restriction data.');
    }

    return new Response(
      new Blob([entry.payload]).stream().pipeThrough(new DecompressionStream('gzip')),
    ).text();
  }

  if (typeof entry.payload === 'string') {
    return entry.payload;
  }

  return new TextDecoder().decode(entry.payload);
}

async function fetchRestrictionGeoJsonText() {
  const response = await fetch(`${API_BASE_URL}/restrictions`, {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(`Restriction request failed with HTTP ${response.status}`);
  }

  return response.text();
}

function parseRestrictionFeatures(geoJsonText, projection) {
  const geoJson = JSON.parse(geoJsonText);
  return {
    features: restrictionGeoJsonFormat.readFeatures(geoJson, {
      featureProjection: projection,
    }),
    lastRefreshed: String(geoJson.lastRefreshed || '').trim(),
  };
}

async function loadRestrictionFeatures(projection) {
  startRestrictionStatusActivity('restriction');

  let staleRestrictionData = null;

  try {
    const cacheEntry = await readRestrictionCacheEntry();
    if (cacheEntry) {
      try {
        const cachedPayload = await expandRestrictionPayload(cacheEntry);
        const restrictionData = parseRestrictionFeatures(cachedPayload, projection);
        if (isRestrictionCacheFresh(cacheEntry)) {
          endRestrictionStatusActivity('restriction');
          showRestrictionCacheStatus(restrictionData.lastRefreshed);
          return restrictionData;
        }

        staleRestrictionData = restrictionData;
      } catch (cacheError) {
        console.warn('Ignoring invalid cached restriction data.', cacheError);
        await deleteRestrictionCacheEntry();
      }
    }

    const freshPayload = await fetchRestrictionGeoJsonText();

    try {
      await writeRestrictionCacheEntry(freshPayload);
    } catch (cacheWriteError) {
      console.warn('Failed to persist restriction cache.', cacheWriteError);
    }

    const restrictionData = parseRestrictionFeatures(freshPayload, projection);
    endRestrictionStatusActivity('restriction');
    showRestrictionDateStatus(restrictionData.lastRefreshed);
    return restrictionData;
  } catch (error) {
    if (staleRestrictionData) {
      console.warn('Restriction network load failed, falling back to stale cache.', error);
      endRestrictionStatusActivity('restriction');
      showRestrictionStaleCacheStatus(staleRestrictionData.lastRefreshed);
      return staleRestrictionData;
    }

    endRestrictionStatusActivity('restriction');
    setRestrictionPersistentStatus({
      message: 'Restriction data failed to load. Reload the page to retry.',
      error: true,
    });
    throw error;
  }
}

function hasRestrictionCode(feature, code) {
  const restrictionType = String(feature.get('restrictionType') || '').trim();
  return restrictionType === code || restrictionType.includes(code);
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function placeLabel(place) {
  return String(place?.label || place?.name || '').trim();
}

function placeMeta(place) {
  return [place?.locality, place?.region].filter((value) => value && String(value).trim()).join(' • ');
}

function createSearchMarkerStyle() {
  return new Style({
    image: new Icon({
      anchor: [0.5, 1],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      imgSize: [30, 42],
      src: `data:image/svg+xml;utf8,${encodeURIComponent(createSearchMarkerSvg())}`,
    }),
  });
}

function createSearchMarkerSvg() {
  return '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42"><path d="M15 1C8.1 1 2.5 6.6 2.5 13.5c0 8.8 10.7 20 11.2 20.5a1.8 1.8 0 0 0 2.6 0c0.5-0.5 11.2-11.7 11.2-20.5C27.5 6.6 21.9 1 15 1Z" fill="#dd3344" stroke="#fff7f7" stroke-width="2"/><circle cx="15" cy="13.5" r="4.5" fill="#fff7f7"/></svg>';
}

function styleRestrictionFeature(feature, _) {

  const restrictionType = feature.get('restrictionType');
  let style = restrictionStyleCache[restrictionType];
  if(!style) {
    style = new Style({
      fill: new Fill({
        color: '#eeeeee',
      })
    });

    const color = feature.get('COLOR') || 'transparent';
    
    style.getFill().setColor(color);
    if (ENABLE_INLINE_RESTRICTION_LABELS) {
      const text = new Text({
        text: feature.get('restrictionDesc') || 'N/A',
        font: '14px sans-serif',
        fill: new Fill({
          color: '#ffffff'
        }),
        stroke: new Stroke({
          color: '#000000',
          width: 3
        }),
      });
      style.setText(text);
    }

    restrictionStyleCache[restrictionType] = style;
  }

    return style;
}

function createOverlayStyle(filterCode) {
  return function overlayStyle(feature, resolution) {
    if (!hasRestrictionCode(feature, filterCode)) {
      return null;
    }

    return styleRestrictionFeature(feature, resolution);
  };
}

function seamarkStyleFunction(feature) {
  const symbol = normalizeText(feature.get('symbol'));
  let style = seamarkStyleCache[symbol];
  if (style === null) {
    return null;
  }

  if (!style) {
    style = createSeamarkSymbolStyle(symbol);
    seamarkStyleCache[symbol] = style;
  }

  return style;
}

function createSeamarkSymbolStyle(symbol) {
  if (symbol === 'd') {
    return [
      new Style({
        image: new RegularShape({
          points: 3,
          radius: 5.5,
          rotation: 0,
          fill: new Fill({
            color: '#12a150',
          }),
          stroke: new Stroke({
            color: '#e8fff1',
            width: 2,
          }),
        }),
      }),
    ];
  }

  if (symbol === 'b') {
    return [
      new Style({
        image: new RegularShape({
          points: 4,
          radius: 5,
          angle: Math.PI / 4,
          fill: new Fill({
            color: '#d53333',
          }),
          stroke: new Stroke({
            color: '#fff1f1',
            width: 2,
          }),
        }),
      }),
    ];
  }

  if (symbol === 'f') {
    return [createCardinalIconStyle('north')];
  }

  if (symbol === 'l') {
    return [createCardinalIconStyle('east')];
  }

  if (symbol === 'i') {
    return [createCardinalIconStyle('west')];
  }

  if (symbol === 'h') {
    return [createCardinalIconStyle('south')];
  }

  return null;
}

function createCardinalIconStyle(direction) {
  return new Style({
    image: new Icon({
      anchor: [0.5, 0.5],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
      imgSize: [22, 22],
      src: `data:image/svg+xml;utf8,${encodeURIComponent(createCardinalSvg(direction))}`,
    }),
  });
}

function createCardinalSvg(direction) {
  const triangles = {
    north: [
      '11,1 4,11 18,11',
      '11,9 4,19 18,19',
    ],
    south: [
      '4,10 18,10 11,0',
      '4,20 18,20 11,10',
    ],
    east: [
      '11,1 4,11 18,11',
      '4,12 18,12 11,22',
    ],
    west: [
      '4,10 18,10 11,0',
      '11,21 4,11 18,11',
    ],
  };

  const points = triangles[direction];
  return `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"><polygon points="${points[0]}" fill="#111111" stroke="#f6df72" stroke-width="1.35"/><polygon points="${points[1]}" fill="#111111" stroke="#f6df72" stroke-width="1.35"/></svg>`;
}

function createBaseLayer({id, title, visible, source}) {
  return new TileLayer({
    cacheSize: 128,
    source,
    visible,
    properties: {
      id,
      title,
      layerRole: 'base',
    },
  });
}

function createXyzBaseLayer({id, title, visible = false, url, attributions, maxZoom = 19}) {
  return createBaseLayer({
    id,
    title,
    visible,
    source: new XYZ({
      url,
      attributions,
      maxZoom,
      crossOrigin: 'anonymous',
    }),
  });
}

function createOverlayLayer({id, title, filterCode, visible = true}) {
  return new VectorImageLayer({
    background: 'transparent',
    source: restrictionSource,
    visible,
    style: createOverlayStyle(filterCode),
    properties: {
      id,
      title,
      filterCode,
      layerRole: 'overlay',
    },
  });
}

function createSeamarkLayer({id, title, visible = false}) {
  return new VectorImageLayer({
    background: 'transparent',
    source: seamarkSource,
    visible,
    style: seamarkStyleFunction,
    properties: {
      id,
      title,
      layerRole: 'overlay',
    },
  });
}

let restrictionLoadPromise = null;
let restrictionLoadCompleted = false;

const restrictionSource = new VectorSource({
  format: restrictionGeoJsonFormat,
  strategy: allLoadingStrategy,
  loader: (_, __, projection, success, failure) => {
    if (restrictionLoadCompleted) {
      success([]);
      return;
    }

    if (!restrictionLoadPromise) {
      restrictionLoadPromise = loadRestrictionFeatures(projection);
    }

    restrictionLoadPromise
      .then((restrictionData) => {
        if (restrictionLoadCompleted) {
          success([]);
          return;
        }

        restrictionLoadCompleted = true;
        restrictionSource.addFeatures(restrictionData.features);
        success(restrictionData.features);
      })
      .catch((error) => {
        console.error('Restriction load failed.', error);
        restrictionLoadPromise = null;
        failure();
      });
  },
});

const seamarkSource = new VectorSource({
  url: `${API_BASE_URL}/seamarks`,
  format: new GeoJSON(),
});

seamarkSource.on('featuresloadstart', () => {
  startRestrictionStatusActivity('seamark');
});

seamarkSource.on('featuresloadend', () => {
  endRestrictionStatusActivity('seamark');
});

seamarkSource.on('featuresloaderror', () => {
  endRestrictionStatusActivity('seamark');
});

const baseLayers = [
  createBaseLayer({
    id: BASE_LAYER_ID_STANDARD,
    title: 'OpenStreetMap Standard',
    visible: true,
    source: new OSM(),
  }),
  createXyzBaseLayer({
    id: BASE_LAYER_ID_OPENTOPO,
    title: 'OpenTopoMap',
    url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attributions: OSM_CONTRIBUTORS_ATTRIBUTION + ', Map style © OpenTopoMap (CC-BY-SA)',
    maxZoom: 17,
  }),
];

const overlayLayers = [
  createOverlayLayer({
    id: OVERLAY_LAYER_ID_SPEED,
    title: 'Restrictions - speed',
    filterCode: '01',
  }),
  createOverlayLayer({
    id: OVERLAY_LAYER_ID_MOTOR,
    title: 'Restrictions - motor ban',
    filterCode: '05',
  }),
  createSeamarkLayer({
    id: OVERLAY_LAYER_ID_BUOYS,
    title: 'Seamarks - buoy markers',
    visible: false,
  }),
];

const baseLayerGroup = new LayerGroup({
  properties: {
    id: 'base-layers',
    title: 'Base layers',
  },
  layers: baseLayers,
});

const dataLayerGroup = new LayerGroup({
  properties: {
    id: 'data-layers',
    title: 'Data layers',
  },
  layers: overlayLayers,
});

var extent = olextent.createEmpty();

function setActiveBaseLayer(selectedLayerId) {
  baseLayers.forEach((layer) => {
    const isSelected = layer.get('id') === selectedLayerId;
    layer.setVisible(isSelected);
  });
}

class LayerSelectorControl extends Control {
  constructor({baseLayerOptions, overlayLayerOptions}) {
    const element = document.createElement('div');
    element.className = 'layer-selector ol-unselectable ol-control';

    const toggleButton = document.createElement('button');
    toggleButton.type = 'button';
    toggleButton.className = 'layer-selector-toggle';
    toggleButton.setAttribute('aria-expanded', 'false');
    toggleButton.setAttribute('aria-label', 'Map layers');
    toggleButton.textContent = '🗺';

    const panel = document.createElement('div');
    panel.className = 'layer-selector-panel';
    panel.hidden = true;

    const panelHeader = document.createElement('div');
    panelHeader.className = 'layer-selector-panel-header';

    const panelHeading = document.createElement('h2');
    panelHeading.className = 'layer-selector-panel-heading';
    panelHeading.textContent = 'Map layers';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'layer-selector-close';
    closeButton.setAttribute('aria-label', 'Close layer selector');
    closeButton.textContent = 'Close';

    panelHeader.append(panelHeading, closeButton);

    const baseSection = document.createElement('section');
    baseSection.className = 'layer-selector-section';

    const baseTitle = document.createElement('h3');
    baseTitle.className = 'layer-selector-title';
    baseTitle.textContent = 'Base layers';
    baseSection.append(baseTitle, createBaseLayerList(baseLayerOptions, () => this.closePanel()));

    const dataSection = document.createElement('section');
    dataSection.className = 'layer-selector-section';

    const dataTitle = document.createElement('h3');
    dataTitle.className = 'layer-selector-title';
    dataTitle.textContent = 'Data layers';
    dataSection.append(dataTitle, createOverlayLayerList(overlayLayerOptions, () => this.closePanel()));

    panel.append(panelHeader, baseSection, dataSection);
    element.append(toggleButton, panel);

    super({element});

    this.panel = panel;
    this.toggleButton = toggleButton;
    this.element = element;

    toggleButton.addEventListener('click', () => {
      this.togglePanel();
    });

    closeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.closePanel();
    });

    panel.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  isMobileLayout() {
    return window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches;
  }

  openPanel() {
    this.panel.hidden = false;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.element.classList.add('is-open');
  }

  closePanel() {
    this.panel.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.element.classList.remove('is-open');
  }

  togglePanel() {
    if (this.panel.hidden) {
      this.openPanel();
      return;
    }

    this.closePanel();
  }
}

class LocateControl extends Control {
  constructor({geolocation, onLocate}) {
    const element = document.createElement('div');
    element.className = 'locate-control ol-unselectable ol-control';

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'locate-control-button';
    button.setAttribute('aria-label', 'Locate me');
    button.title = 'Locate me';
    button.textContent = '◎';

    element.append(button);

    super({element});

    this.button = button;
    this.geolocation = geolocation;
    this.onLocate = onLocate;

    button.addEventListener('click', () => {
      if (typeof this.onLocate === 'function') {
        this.onLocate();
      }
      this.geolocation.setTracking(true);
    });
  }
}

class InfoControl extends Control {
  constructor() {
    const element = document.createElement('div');
    element.className = 'info-control ol-unselectable ol-control';

    const toggleButton = document.createElement('button');
    toggleButton.type = 'button';
    toggleButton.className = 'info-control-toggle';
    toggleButton.setAttribute('aria-expanded', 'false');
    toggleButton.setAttribute('aria-label', 'Legal information');
    toggleButton.title = 'Legal information';
    toggleButton.textContent = 'i';

    const panel = document.createElement('div');
    panel.className = 'info-control-panel';
    panel.hidden = true;

    const header = document.createElement('div');
    header.className = 'info-control-panel-header';

    const heading = document.createElement('h2');
    heading.className = 'info-control-panel-heading';
    heading.textContent = 'Legal information';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'info-control-close';
    closeButton.setAttribute('aria-label', 'Close legal information');
    closeButton.textContent = 'Close';

    header.append(heading, closeButton);

    const list = document.createElement('div');
    list.className = 'info-control-links';

    [
      {href: '/privacy-policy.html', label: 'Privacy Policy (English)'},
      {href: '/tietosuojaseloste.html', label: 'Tietosuojaseloste (Suomi)'},
      {href: '/disclaimer.html', label: 'Disclaimer'},
    ].forEach((linkDefinition) => {
      const link = document.createElement('a');
      link.className = 'info-control-link';
      link.href = linkDefinition.href;
      link.textContent = linkDefinition.label;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      list.append(link);
    });

    panel.append(header, list);
    element.append(toggleButton, panel);

    super({element});

    this.element = element;
    this.toggleButton = toggleButton;
    this.panel = panel;

    toggleButton.addEventListener('click', () => {
      this.togglePanel();
    });

    closeButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.closePanel();
    });

    panel.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  openPanel() {
    this.panel.hidden = false;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.element.classList.add('is-open');
  }

  closePanel() {
    this.panel.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.element.classList.remove('is-open');
  }

  togglePanel() {
    if (this.panel.hidden) {
      this.openPanel();
      return;
    }

    this.closePanel();
  }
}

function createBaseLayerList(layers, onSelect) {
  const list = document.createElement('div');
  list.className = 'layer-selector-list';

  layers.forEach((layer) => {
    const label = document.createElement('label');
    label.className = 'layer-selector-option';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'base-layer';
    input.checked = layer.getVisible();
    input.addEventListener('change', () => {
      if (input.checked) {
        setActiveBaseLayer(layer.get('id'));
        if (window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches) {
          onSelect();
        }
      }
    });

    const text = document.createElement('span');
    text.textContent = layer.get('title');

    label.append(input, text);
    list.append(label);
  });

  return list;
}

function createOverlayLayerList(layers, onSelect) {
  const list = document.createElement('div');
  list.className = 'layer-selector-list';

  layers.forEach((layer) => {
    const label = document.createElement('label');
    label.className = 'layer-selector-option';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.checked = layer.getVisible();
    input.addEventListener('change', () => {
      layer.setVisible(input.checked);
      if (window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches) {
        onSelect();
      }
    });

    const text = document.createElement('span');
    text.textContent = layer.get('title');

    label.append(input, text);
    list.append(label);
  });

  return list;
}

const geolocation = new Geolocation({
  // enableHighAccuracy must be set to true to have the heading value.
  trackingOptions: {
    enableHighAccuracy: true,
  },
  projection: view.getProjection(),
});

const layerSelectorControl = new LayerSelectorControl({
  baseLayerOptions: baseLayers,
  overlayLayerOptions: overlayLayers,
});

const locateControl = new LocateControl({
  geolocation,
  onLocate: () => {
    clearSearchMarker();
    clearSearchStatus();
    cancelAutocompleteRequest();
    if (searchState) {
      window.clearTimeout(searchState.autocompleteTimer);
      searchState.selectedPlace = null;
      searchState.suggestions = [];
    }
    if (suggestionList) {
      suggestionList.hidden = true;
    }

    const position = geolocation.getPosition();
    const accuracyGeometry = geolocation.getAccuracyGeometry();
    if (accuracyGeometry) {
      map.getView().fit(accuracyGeometry, {
        duration: 350,
        maxZoom: 14,
        padding: [48, 48, 48, 48],
      });
      return;
    }

    if (position) {
      map.getView().animate({
        center: position,
        zoom: Math.max(map.getView().getZoom() ?? 10, 14),
        duration: 350,
      });
    }
  },
});
const infoControl = new InfoControl();

const map = new Map({
  pixelRatio: 1,
  controls: defaultControls().extend([locateControl, layerSelectorControl, infoControl]),
  layers: [
    baseLayerGroup,
    dataLayerGroup,
  ],
  target: 'map',
  view: view,
});

const searchMarkerFeature = new Feature();
searchMarkerFeature.setStyle(createSearchMarkerStyle());

new VectorLayer({
  map,
  source: new VectorSource({
    features: [searchMarkerFeature],
  }),
});

const searchState = {
  activeController: null,
  autocompleteTimer: 0,
  suggestions: [],
  selectedPlace: null,
};

const searchToggleElement = document.createElement('div');
searchToggleElement.className = 'search-toggle-control ol-unselectable ol-control';

const searchToggleButton = document.createElement('button');
searchToggleButton.type = 'button';
searchToggleButton.className = 'search-toggle-button';
searchToggleButton.setAttribute('aria-expanded', 'false');
searchToggleButton.setAttribute('aria-label', 'Search addresses');
searchToggleButton.textContent = '⌕';
searchToggleElement.append(searchToggleButton);

map.addControl(new Control({element: searchToggleElement}));

const searchPanel = document.createElement('div');
searchPanel.className = 'map-search-panel';
searchPanel.hidden = true;

const searchForm = document.createElement('form');
searchForm.className = 'map-search-form';

const searchInput = document.createElement('input');
searchInput.type = 'search';
searchInput.className = 'map-search-input';
searchInput.placeholder = 'Search address';
searchInput.autocomplete = 'off';
searchInput.spellcheck = false;
searchInput.setAttribute('aria-label', 'Search address');

const searchSubmitButton = document.createElement('button');
searchSubmitButton.type = 'submit';
searchSubmitButton.className = 'map-search-submit';
searchSubmitButton.textContent = 'Search';

searchForm.append(searchInput, searchSubmitButton);

const searchStatus = document.createElement('div');
searchStatus.className = 'map-search-status';
searchStatus.hidden = true;

const suggestionList = document.createElement('div');
suggestionList.className = 'map-search-suggestions';
suggestionList.hidden = true;

searchPanel.append(searchForm, suggestionList, searchStatus);
map.getTargetElement().append(searchPanel);

function setSearchPanelOpen(nextOpen) {
  searchPanel.hidden = !nextOpen;
  searchToggleButton.setAttribute('aria-expanded', String(nextOpen));
  searchToggleElement.classList.toggle('is-open', nextOpen);
  if (nextOpen) {
    searchInput.focus();
    searchInput.select();
  } else {
    suggestionList.hidden = true;
  }
}

function clearSearchStatus() {
  searchStatus.hidden = true;
  searchStatus.textContent = '';
}

function setSearchStatus(message) {
  searchStatus.hidden = !message;
  searchStatus.textContent = message || '';
}

function clearSearchMarker() {
  searchMarkerFeature.setGeometry(null);
}

function renderSuggestions(places) {
  suggestionList.replaceChildren();
  if (!places.length) {
    suggestionList.hidden = true;
    return;
  }

  places.forEach((place) => {
    const itemButton = document.createElement('button');
    itemButton.type = 'button';
    itemButton.className = 'map-search-suggestion';

    const title = document.createElement('span');
    title.className = 'map-search-suggestion-title';
    title.textContent = placeLabel(place);

    const meta = placeMeta(place);
    itemButton.append(title);

    if (meta) {
      const metaText = document.createElement('span');
      metaText.className = 'map-search-suggestion-meta';
      metaText.textContent = meta;
      itemButton.append(metaText);
    }

    itemButton.addEventListener('click', () => {
      searchState.selectedPlace = place;
      searchInput.value = placeLabel(place);
      suggestionList.hidden = true;
      setPlaceMarker(place);
    });

    suggestionList.append(itemButton);
  });

  suggestionList.hidden = false;
}

function getSearchFocusCoordinates() {
  if (!geolocation.getPosition()) {
    return null;
  }

  const [longitude, latitude] = toLonLat(geolocation.getPosition(), view.getProjection());
  return {latitude, longitude};
}

function buildPlaceQueryParams(queryText) {
  const params = new URLSearchParams({
    query: queryText,
    layers: PLACE_AUTOCOMPLETE_LAYERS,
    lang: DEFAULT_PLACE_SEARCH_LANG,
    size: '8',
  });

  const focusCoordinates = getSearchFocusCoordinates();
  if (focusCoordinates) {
    params.set('focusLat', focusCoordinates.latitude.toFixed(5));
    params.set('focusLon', focusCoordinates.longitude.toFixed(5));
  }

  return params;
}

function cancelAutocompleteRequest() {
  if (searchState.activeController) {
    searchState.activeController.abort();
    searchState.activeController = null;
  }
}

async function fetchPlaceMatches(endpoint, queryText) {
  cancelAutocompleteRequest();
  const controller = new AbortController();
  searchState.activeController = controller;

  const response = await fetch(`${API_BASE_URL}/places/${endpoint}?${buildPlaceQueryParams(queryText).toString()}`, {
    signal: controller.signal,
  });
  if (!response.ok) {
    const error = new Error(`Place lookup failed with status ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const data = await response.json();
  if (searchState.activeController === controller) {
    searchState.activeController = null;
  }

  return Array.isArray(data?.places) ? data.places : [];
}

function setPlaceMarker(place) {
  if (place?.longitude == null || place?.latitude == null) {
    setSearchStatus('Selected result has no map coordinates.');
    return;
  }

  clearSearchStatus();
  const coordinate = fromLonLat([place.longitude, place.latitude], view.getProjection());
  searchMarkerFeature.setGeometry(new Point(coordinate));
  map.getView().animate({
    center: coordinate,
    zoom: Math.max(map.getView().getZoom() ?? 10, 14),
    duration: 350,
  });
}

async function submitPlaceSearch() {
  const queryText = String(searchInput.value || '').trim();
  if (!queryText) {
    setSearchStatus('Enter an address to search.');
    return;
  }

  clearSearchStatus();
  suggestionList.hidden = true;

  const exactSuggestion = searchState.suggestions.find((place) => placeLabel(place) === queryText);
  if (searchState.selectedPlace && placeLabel(searchState.selectedPlace) === queryText) {
    setPlaceMarker(searchState.selectedPlace);
    return;
  }

  if (exactSuggestion) {
    searchState.selectedPlace = exactSuggestion;
    setPlaceMarker(exactSuggestion);
    return;
  }

  try {
    searchSubmitButton.disabled = true;
    const places = await fetchPlaceMatches('search', queryText);
    if (!places.length) {
      setSearchStatus('No matching address found.');
      clearSearchMarker();
      return;
    }

    searchState.selectedPlace = places[0];
    searchInput.value = placeLabel(places[0]);
    setPlaceMarker(places[0]);
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error(error);
      setSearchStatus('Address search is currently unavailable.');
    }
  } finally {
    searchSubmitButton.disabled = false;
  }
}

searchToggleButton.addEventListener('click', () => {
  setSearchPanelOpen(searchPanel.hidden);
});

searchPanel.addEventListener('click', (event) => {
  event.stopPropagation();
});

searchForm.addEventListener('submit', (event) => {
  event.preventDefault();
  submitPlaceSearch();
});

searchInput.addEventListener('input', () => {
  const queryText = String(searchInput.value || '').trim();
  searchState.selectedPlace = null;
  clearSearchStatus();

  window.clearTimeout(searchState.autocompleteTimer);
  if (queryText.length < 2) {
    searchState.suggestions = [];
    renderSuggestions([]);
    cancelAutocompleteRequest();
    return;
  }

  searchState.autocompleteTimer = window.setTimeout(async () => {
    try {
      const places = await fetchPlaceMatches('autocomplete', queryText);
      searchState.suggestions = places;
      renderSuggestions(places);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
        searchState.suggestions = [];
        renderSuggestions([]);
        setSearchStatus(error.status === 429
          ? 'Address search is currently unavailable.'
          : 'Address suggestions are currently unavailable.');
      }
    }
  }, PLACE_AUTOCOMPLETE_DELAY_MS);
});
geolocation.on('change:accuracyGeometry', function() {
  
    geolocation.getAccuracyGeometry().getExtent(extent);
    extent[0] = extent[0] - 20000;
    extent[1] = extent[1] - 20000;
    extent[2] = extent[2] + 20000;
    extent[3] = extent[3] + 20000;
});

let highlight;

function joinFeatureDetails(parts) {
  return parts.filter((value) => value && String(value).trim()).join(' • ');
}

function formatRestrictionInfo(feature) {
  return joinFeatureDetails([
    feature.get('restrictionDesc'),
    feature.get('restrictionAddInfo'),
    feature.get('restrictionValidity'),
  ]);
}

function formatSeamarkInfo(feature) {
  return joinFeatureDetails([
    feature.get('nameFi') || feature.get('nameSv') || feature.get('seamarkNumber'),
    feature.get('seamarkType'),
    feature.get('locationFi') || feature.get('locationSv'),
    feature.get('fairwayName'),
    feature.get('symbol') ? `symbol ${feature.get('symbol')}` : '',
    feature.get('topmark') ? `topmark ${feature.get('topmark')}` : '',
  ]);
}

function hideFeatureInfo() {
  const info = document.getElementById('info');
  if (!info) {
    return;
  }

  info.textContent = '';
  info.classList.add('is-hidden');
}

function displayFeatureInfo(pixel) {
  const feature = map.forEachFeatureAtPixel(pixel, function (feature) {
    return feature;
  });

  const info = document.getElementById('info');
  if (!info) {
    return;
  }

  if (!feature) {
    hideFeatureInfo();
    return;
  }

  const infoText = feature.get('seamarkType')
    ? formatSeamarkInfo(feature)
    : formatRestrictionInfo(feature);

  info.textContent = infoText || 'No details available';
  info.classList.remove('is-hidden');
}

map.on('click', function (evt) {
  displayFeatureInfo(evt.pixel);
});

function el(id) {
  return document.getElementById(id);
}

/*el('track').addEventListener('change', function () {
  geolocation.setTracking(this.checked);
});*/

// update the HTML page when the position changes.
geolocation.on('change', function () {
  
  el('currentpos').innerText = toStringHDMS(geolocation.getPosition());
  el('heading').innerText = Math.round(geolocation.getHeading() * (180/Math.PI)) + ' [deg]';
  el('speed').innerText = (geolocation.getSpeed() * 1.94384449).toPrecision(2)  + ' [kts]';
});

// handle geolocation error.
geolocation.on('error', function (error) {
  const info = document.getElementById('info');
  info.textContent = error.message;
  info.classList.remove('is-hidden');
});

const accuracyFeature = new Feature();
geolocation.on('change:accuracyGeometry', function () {
  accuracyFeature.setGeometry(geolocation.getAccuracyGeometry());
});

const positionFeature = new Feature();
positionFeature.setStyle(
  new Style({
    image: new CircleStyle({
      radius: map.getView().getZoom(),
      fill: new Fill({
        color: '#3399CC',
      }),
      stroke: new Stroke({
        color: '#fff',
        width: 5,
      }),
    }),
  })
);

geolocation.on('change:position', function () {
  const coordinates = geolocation.getPosition();
  positionFeature.setGeometry(coordinates ? new Point(coordinates) : null);
});

new VectorLayer({
  map: map,
  source: new VectorSource({
    features: [accuracyFeature, positionFeature],
  }),
});
